import {
  useMutation, useQuery, useQueryClient, UseQueryResult,
} from '@tanstack/react-query';

import { axiosInstance } from '@/axios';
import { STRIPE_CURRENCY_STATUS_QUERY_KEY } from '@/components/StripeCurrencyMismatchBanner/queryKeys';
import { responseHandler } from '@/utils';

/**
 * Płatności online POJEDYNCZEGO outletu (Stripe Connect).
 *
 * Komponent jest samowystarczalny — ma własne hooki, tak jak `OutletCurrencySection`.
 * Ten sam ekran obsłuży docelowo cztery moduły, a każdy ma kontekst o innym kształcie.
 *
 * Cały stan czytamy z NASZEGO backendu, nie ze Stripe'a: dane utrzymuje webhook
 * `account.updated`, więc ekran ustawień nie płaci przy każdym wejściu połączeniem do
 * api.stripe.com i działa nawet wtedy, gdy Stripe ma awarię.
 */

export type OutletPaymentKind =
  | 'roomService'
  | 'productsServices'
  | 'productRental'
  | 'spa'
  | 'breakfastService';

export const OUTLET_PAYMENTS_QUERY_KEY = 'outlet-payments';
export const HOTEL_STRIPE_ACCOUNTS_QUERY_KEY = 'hotel-stripe-accounts';

/** Koperta odpowiedzi backendu v3 (`responseHandler` po stronie funkcji). */
interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface OutletPaymentConfig {
  connected: boolean;
  accountId?: string;
  displayName?: string | null;
  feePercent: number;
  currency?: string | null;
  /** Waluta konta Stripe (lustro konta); `null`, gdy Stripe jej nie podał. */
  accountCurrency?: string | null;
  /** Stan lustra płatności online punktu (#274): `false` także przy niezgodnej walucie. */
  onlinePaymentsEnabled?: boolean;
  /** Konto zmieniło walutę albo punkt dostał inną: online wraca dopiero po ponownym wpięciu. */
  currencyMismatch?: boolean;
  chargesEnabled?: boolean;
  /** `false` = konto w sandboxie / trybie testowym Stripe'a; `null` = nieznane (stary wpis). */
  livemode?: boolean | null;
  payoutsEnabled?: boolean;
  detailsSubmitted?: boolean;
  /** `requirements.currently_due` ze Stripe'a — czego konto jeszcze nie dostarczyło. */
  requirementsDue?: string[];
  /** Domyślna metoda punktu — przy „online" odłączenie wymaga wskazania następczyni. */
  defaultPaymentMethod?: 'cash' | 'card' | 'online' | null;
  /** Metody przy odbiorze włączone w punkcie (kandydatki na nową domyślną). */
  offlineMethods?: Array<'cash' | 'card'>;
}

/** Punkt sprzedaży wpięty w konto — backend odsiewa wpisy po skasowanych punktach. */
export interface LinkedOutlet {
  outletId: string;
  kind: OutletPaymentKind;
  /** Nazwa z dokumentu punktu; `null`, gdy punkt jej nie ma. */
  name: string | null;
}

export interface HotelStripeAccount {
  accountId: string;
  displayName: string | null;
  country: string | null;
  currency: string | null;
  chargesEnabled: boolean;
  /** Gotowość do przyjęcia płatności (charges_enabled i aktywne card_payments). */
  ready: boolean;
  /**
   * Zweryfikowane i aktywne: płatności i wypłaty włączone, formularz zakończony, brak
   * zaległych wymagań Stripe'a. TYLKO takie konto wolno wpiąć w punkt (backend to wymusza).
   */
  verified: boolean;
  /** `false` = sandbox / tryb testowy; `null` = konto sprzed zapisu tego pola. */
  livemode: boolean | null;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  /** Onboarding zaczęty, formularz Stripe'a niedokończony — pokazujemy „dokończ konfigurację". */
  onboardingInProgress: boolean;
  /** Hotel odłączył platformę w swoim dashboardzie Stripe'a — na tym koncie nic już nie powstanie. */
  deauthorized: boolean;
  requirementsDue: string[];
  /** Ile usług hotelu korzysta już z tego konta. */
  outletsCount: number;
  /** Które to usługi — po tym widać, gdzie iść, żeby zwolnić konto przed usunięciem. */
  outlets: LinkedOutlet[];
}

/**
 * Ustawienia płatności CAŁEJ organizacji: `available` ustawia HotelSpot, `enabled`
 * hotelarz akceptując regulamin. Podpiąć konto w punkcie można dopiero, gdy oba są
 * prawdziwe — dlatego to jest osobne zapytanie, a nie pole konfiguracji punktu.
 */
export interface StripeOrgSettings {
  available: boolean;
  enabled: boolean;
  /** Domyślna stawka prowizji HotelSpot dla całego obiektu, w procentach. */
  feePercent: number;
  /** Nadpisania stawki dla wybranych typów punktów: `{ spa: 3 }`. Brak wpisu = stawka domyślna. */
  feeByKind?: Partial<Record<OutletPaymentKind, number>>;
  currentTermsVersion: string;
  /** Regulamin zmienił się od czasu akceptacji — trzeba poprosić o zgodę na nowo. */
  termsOutdated: boolean;
  terms: {
    version: string;
    /** Milisekundy. */
    acceptedAt: number | null;
    acceptedBy: string;
    acceptedByEmail: string | null;
    acceptedByName?: string | null;
  } | null;
}

export const useGetOutletPaymentConfig = (
  hotelId: string | null | undefined,
  outletId: string | null | undefined,
): UseQueryResult<OutletPaymentConfig, Error> => useQuery<OutletPaymentConfig, Error>({
  queryKey: [OUTLET_PAYMENTS_QUERY_KEY, hotelId, outletId],
  enabled: !!hotelId && !!outletId,
  retry: 0,
  queryFn: () => axiosInstance
    .get<Envelope<OutletPaymentConfig>>('/v3-getOutletPaymentConfig', { params: { hotelId, outletId } })
    .then(responseHandler)
    .then((envelope) => envelope.data),
});

/**
 * Konta Stripe podpięte już w tym hotelu. Pobieramy je TYLKO gdy outlet nie ma jeszcze
 * swojego — służą wyłącznie do wyboru „podłącz do konta, które już masz".
 */
export const useListHotelStripeAccounts = (
  hotelId: string | null | undefined,
  enabled = true,
): UseQueryResult<HotelStripeAccount[], Error> => useQuery<HotelStripeAccount[], Error>({
  queryKey: [HOTEL_STRIPE_ACCOUNTS_QUERY_KEY, hotelId],
  enabled: !!hotelId && enabled,
  retry: 0,
  queryFn: () => axiosInstance
    .get<Envelope<{ accounts: HotelStripeAccount[] }>>('/v3-listHotelStripeAccounts', { params: { hotelId } })
    .then(responseHandler)
    .then((envelope) => envelope.data.accounts ?? []),
});

interface StartConnectParams {
  hotelId: string;
  /** Adres, na który Stripe ma odesłać po zakończeniu — backend waliduje go po origin-ie. */
  returnPath: string;
  /** Podpowiedź do formularza Stripe'a (e-mail obiektu); hotelarz może ją zmienić. */
  email?: string;
  /** ISO alpha-2 kraju konta; brak = Polska. */
  country?: string;
}

/**
 * Start onboardingu konta Stripe HOTELU (Connect Onboarding / Account Links). Nazwa
 * endpointu została z czasów OAuth. Backend zakłada konto i zwraca jednorazowy link do
 * formularza Stripe'a; przy ponownym kliknięciu wraca do zaczętego konta zamiast zakładać
 * nowe, a hotelowi z podpiętym kontem odmawia (jedno konto rozliczeniowe na hotel).
 * Woła się to WYŁĄCZNIE z ekranu integracji — punkty tylko włączają płatności.
 */
export const useCreateStripeOAuthLink = () => useMutation({
  mutationFn: (params: StartConnectParams) => axiosInstance
    .post<Envelope<{ url: string }>>('/v3-createStripeOAuthLink', params)
    .then(responseHandler)
    .then((envelope) => envelope.data.url),
});

interface LinkParams {
  hotelId: string;
  outletId: string;
  kind: OutletPaymentKind;
  /**
   * Bez wskazania backend bierze JEDYNE konto hotelu — tak woła ekran punktu. Jawne
   * `accountId` zostaje dla ekranu integracji („Wepnij usługę" przy konkretnym koncie).
   */
  accountId?: string;
}

/**
 * Wpięcie, odpięcie i włączenie płatności zmieniają listę punktów w monicie o walucie
 * konta Stripe (B3), więc po sukcesie unieważniamy jego stan dla hotelu.
 */
const useInvalidateCurrencyStatus = () => {
  const queryClient = useQueryClient();
  return (hotelId: string): void => {
    queryClient.invalidateQueries({ queryKey: [STRIPE_CURRENCY_STATUS_QUERY_KEY, hotelId] });
  };
};

/** Włączenie płatności online w punkcie na koncie hotelu. */
export const useLinkOutletToStripeAccount = () => {
  const invalidate = useInvalidateCurrencyStatus();
  return useMutation({
    mutationFn: (params: LinkParams) => axiosInstance
      .post('/v3-linkOutletToStripeAccount', params)
      .then(responseHandler),
    onSuccess: (_data, params) => invalidate(params.hotelId),
  });
};

export const useUnlinkOutletStripe = () => {
  const invalidate = useInvalidateCurrencyStatus();
  return useMutation({
    mutationFn: (params: {
      hotelId: string;
      outletId: string;
      /** Wymagane, gdy domyślną metodą punktu jest „online". */
      nextDefaultPaymentMethod?: 'cash' | 'card';
    }) => axiosInstance
      .post('/v3-unlinkOutletStripe', params)
      .then(responseHandler),
    onSuccess: (_data, params) => invalidate(params.hotelId),
  });
};

/**
 * Odpięcie konta Stripe od hotelu — kasuje nasz wpis (konto zostaje własnością hotelu
 * u Stripe'a; niedokończony onboarding backend sprząta też tam).
 * Backend odmówi, dopóki jakikolwiek punkt korzysta z tego konta.
 */
export const useDisconnectStripeAccount = () => {
  const invalidate = useInvalidateCurrencyStatus();
  return useMutation({
    mutationFn: (params: { hotelId: string; accountId: string }) => axiosInstance
      .post('/v3-disconnectStripeAccount', params)
      .then(responseHandler),
    onSuccess: (_data, params) => invalidate(params.hotelId),
  });
};

export const STRIPE_ORG_SETTINGS_QUERY_KEY = 'stripe-org-settings';

export const useGetStripeOrgSettings = (
  hotelId: string | null | undefined,
  enabled = true,
): UseQueryResult<StripeOrgSettings, Error> => useQuery<StripeOrgSettings, Error>({
  queryKey: [STRIPE_ORG_SETTINGS_QUERY_KEY, hotelId],
  enabled: !!hotelId && enabled,
  retry: 0,
  queryFn: () => axiosInstance
    .get<Envelope<StripeOrgSettings>>('/v3-getStripeOrgSettings', { params: { hotelId } })
    .then(responseHandler)
    .then((envelope) => envelope.data),
});

export const STRIPE_TERMS_QUERY_KEY = 'stripe-terms';

/** Sekcja regulaminu: tytuł i jego akapity. Kształt ustala backend przy publikacji. */
export interface StripeTermsSection {
  title: string;
  paragraphs: string[];
}

export interface StripeTermsDocument {
  version: string;
  /** `null`, dopóki HotelSpot nie opublikował własnej wersji — panel bierze wtedy wbudowaną. */
  sections: StripeTermsSection[] | null;
}

/**
 * Treść obowiązującego regulaminu płatności. Pobierana leniwie, dopiero gdy modal ma się
 * pokazać: to kilkanaście tysięcy znaków, których hotel przez większość wizyt w panelu
 * nie ogląda.
 */
export const useGetStripeTerms = (enabled: boolean): UseQueryResult<StripeTermsDocument, Error> => useQuery<
  StripeTermsDocument,
  Error
>({
  queryKey: [STRIPE_TERMS_QUERY_KEY],
  enabled,
  retry: 0,
  staleTime: 5 * 60 * 1000,
  queryFn: () => axiosInstance
    .get<Envelope<StripeTermsDocument>>('/v3-getStripeTerms')
    .then(responseHandler)
    .then((envelope) => envelope.data),
});

/**
 * Odmowa nowej wersji regulaminu.
 *
 * Nie jest to zwykłe „zamknij okno": backend odpina WSZYSTKIE punkty od kont Stripe
 * i gasi płatności online, bo gość nie może płacić na warunkach, których obiekt właśnie
 * nie przyjął. Konta zostają podpięte do hotelu, więc powrót po akceptacji to ponowne
 * wskazanie punktów, nie przechodzenie OAuth od nowa.
 */
export const useDeclineStripeTerms = () => useMutation({
  mutationFn: (params: { hotelId: string }) => axiosInstance
    .post('/v3-declineStripeTerms', params)
    .then(responseHandler),
});

/**
 * Włączenie płatności przez hotel. `acceptTerms` wysyłamy przy pierwszym włączeniu
 * i po każdej zmianie wersji regulaminu — backend zapisuje wtedy, KTO i KIEDY się zgodził.
 */
export const useSetStripePaymentsEnabled = () => {
  const invalidate = useInvalidateCurrencyStatus();
  return useMutation({
    mutationFn: (params: { hotelId: string; enabled: boolean; acceptTerms?: boolean }) => axiosInstance
      .post('/v3-setStripePaymentsEnabled', params)
      .then(responseHandler),
    onSuccess: (_data, params) => invalidate(params.hotelId),
  });
};
